import { defineStore } from 'pinia'
import streamerApi from '../api/streamerApi'
import {
    PlayerResolution,
    playerResolutionDimensions,
} from '../enums/playerResolution'
import { useCursorStore } from './cursor'
import { useMachineStore } from './machine'
import { useStore } from './main'
import { useUserStore } from './user'
import { useVdAgentStore } from './vdAgent'

interface VideoDimensions {
    remoteWidth: number
    remoteHeight: number
    localWidth: number
    localHeight: number
}

interface State {
    windowSizeChangeTimeout: number | null
    remoteDimensionsUpdateTimeout: number | null
    videoDimensions: VideoDimensions
}

export const useResolutionStore = () => {
    const store = defineStore('resolution', {
        state: (): State => ({
            windowSizeChangeTimeout: null,
            remoteDimensionsUpdateTimeout: null,
            videoDimensions: {
                remoteWidth: 0,
                remoteHeight: 0,
                localWidth: 0,
                localHeight: 0,
            },
        }),

        actions: {
            initRemoteResolution() {
                this.updateRemoteResolution()

                // Setup resize listener for resolution management
                window.addEventListener('resize', () => {
                    if (this.windowSizeChangeTimeout) {
                        clearTimeout(this.windowSizeChangeTimeout)
                    }

                    this.windowSizeChangeTimeout = window.setTimeout(() => {
                        this.updateRemoteResolution()

                        this.windowSizeChangeTimeout = null
                    }, 500)
                })
            },

            updateRemoteResolution() {
                const mainStore = useStore()
                const vdAgentStore = useVdAgentStore()

                if (!vdAgentStore.isEnabled || mainStore.readonly) {
                    return
                }

                const videoElement = document.getElementById(
                    'video'
                ) as HTMLVideoElement
                if (!videoElement) {
                    throw new Error('#video element has not been found.')
                }

                const userStore = useUserStore()
                const machineStore = useMachineStore()

                // Fallback to AUTO if user preference is not in machine
                // available resolutions
                const preferredResolution =
                    machineStore.machine.available_player_resolutions.includes(
                        userStore.user.player_preferences.resolution
                    )
                        ? userStore.user.player_preferences.resolution
                        : PlayerResolution.AUTO

                let resolutionDimensions =
                    playerResolutionDimensions(preferredResolution)

                // Auto: compute dimensions from window size and upscale preference
                if (preferredResolution === PlayerResolution.AUTO) {
                    const windowWidth = window.innerWidth
                    const windowHeight = window.innerHeight
                    const isUpscaleModeEnabled =
                        userStore.user.player_preferences
                            .is_upscale_mode_enabled

                    switch (true) {
                        case isUpscaleModeEnabled && windowWidth > 1920:
                            resolutionDimensions = {
                                width: 1920,
                                height: Math.round(
                                    1920 / (windowWidth / windowHeight)
                                ),
                            }
                            break
                        case isUpscaleModeEnabled && windowHeight > 1080:
                            resolutionDimensions = {
                                width: Math.round(
                                    1080 * (windowWidth / windowHeight)
                                ),
                                height: 1080,
                            }
                            break
                        case isUpscaleModeEnabled:
                            resolutionDimensions = {
                                width: windowWidth,
                                height: windowHeight,
                            }
                            break
                        default:
                            resolutionDimensions = {
                                width: Math.min(windowWidth, 1920),
                                height: Math.min(windowHeight, 1080),
                            }
                            break
                    }
                }

                // Override dimensions to use numbers only
                // This a well known technical constraint in video streaming to
                // avoid encoding issue and to ensure compatibility
                resolutionDimensions.width =
                    Math.floor(resolutionDimensions.width / 2) * 2
                resolutionDimensions.height =
                    Math.floor(resolutionDimensions.height / 2) * 2

                streamerApi.updateResolution(
                    resolutionDimensions.width,
                    resolutionDimensions.height
                )

                // Update video dimensions
                this.updateVideoDimensions(
                    videoElement.videoWidth,
                    videoElement.videoHeight
                )

                if (!machineStore.isRunningOnAndroid) {
                    const cursorStore = useCursorStore()

                    cursorStore.scaleCursorForLocalDisplay()
                }
            },

            updateVideoDimensions(remoteWidth: number, remoteHeight: number) {
                // Update local dimensions immediately from window
                this.videoDimensions.localWidth = window.innerWidth
                this.videoDimensions.localHeight = window.innerHeight

                // Clear existing timeout to implement debounce
                if (this.remoteDimensionsUpdateTimeout) {
                    clearTimeout(this.remoteDimensionsUpdateTimeout)
                }

                // Update remote dimensions with debounce
                this.remoteDimensionsUpdateTimeout = window.setTimeout(() => {
                    this.videoDimensions.remoteWidth = remoteWidth
                    this.videoDimensions.remoteHeight = remoteHeight
                    this.remoteDimensionsUpdateTimeout = null
                }, 1000)
            },
        },
    })

    return store()
}
